import { LocalizationService } from '@abp/ng.core';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { DepartmentDto } from 'src/core/models/administration/department/department.dto';
import { CAUserDto } from 'src/core/models/administration/user/CAUser.dto';

@Component({
  selector: 'ca-user-popup-selector',
  templateUrl: './user-popup-selector.component.html',
  styleUrls: ['./user-popup-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => UserPopupSelectorComponent),
    },
  ],
  standalone: false,
})
export class UserPopupSelectorComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit, ControlValueAccessor
{
  @ViewChild(NgSelectComponent) selector: NgSelectComponent;

  @Input()
  userType: 'all' | 'agent' | 'evaluator' = 'all';

  @Input()
  buttonPosition: 'start' | 'end' = 'end';

  @Input()
  dropdownPosition: 'auto' | 'top' | 'bottom' = 'auto';

  @Input()
  addClassToDropdown: string;

  @Input()
  itemsPerPage: number = 9999;

  @Input()
  enabledUserStatusFilter: boolean = true;

  @Input()
  status: 'all' | 'active' | 'passive' = 'active';

  @ViewChild('modal')
  modal: any;

  statusItems = [
    { id: 1, label: this.localizationService.instant('::Active'), class: 'active-filter-icon' },
    { id: 2, label: this.localizationService.instant('::Passive'), class: 'passive-filter-icon' },
    { id: 3, label: this.localizationService.instant('::All'), class: 'all-filter-icon' },
  ];
  currentStatus = this.localizationService.instant('::Active');
  disableTooltip = false;

  changes: MutationObserver;
  classListArray: string[] = [];
  departmentId: number | null;
  departmentSearchKeyword: string | null;
  userSearchKeyword: string | null;
  selectedUsers: CAUserDto[] = [];
  isDisabled: boolean = false;
  value: CAUserDto[] = [];
  placeholder: string = '';
  placeholders: unknown = {
    all: 'AbpUi::UserPopupSelector:SearchAndSelectUsers',
    agent: 'AbpUi::UserPopupSelector:SearchAndSelectAgents',
    evaluator: 'AbpUi::UserPopupSelector:SearchAndSelectEvaluators',
  };
  userPhotoComponentStyles: any = {
    width: '100%',
    height: '100%',
    'border-radius': '50%',
  };

  private searchDepartmentTimer: any;
  private searchUserTimer: any;

  get classList(): string {
    let result = this.classListArray.join(' ');

    if (
      this.elementRef &&
      this.elementRef.nativeElement &&
      this.elementRef.nativeElement.classList
    ) {
      result = result + ' ' + this.elementRef.nativeElement.classList.value;
    }

    return result;
  }

  constructor(
    private modalService: NgbModal,
    private elementRef: ElementRef,
    private localizationService: LocalizationService
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    this.placeholder = this.placeholders[this.userType];
  }

  ngOnDestroy(): void {}

  writeValue(obj: CAUserDto[]): void {
    obj = obj ?? [];

    this.value = new Array(...obj);
    this.selectedUsers = new Array(...obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onChange(eventargs: any): void {}

  onClear(eventArgs: any): void {
    this.value = [];
    this.onChange(this.value);
  }

  onOpen(eventArgs: any): void {
    this.departmentId = null;
    this.selectedUsers = new Array(...this.value);

    this.modalService
      .open(this.modal, {
        size: 'xl',
        scrollable: false,
        backdrop: 'static',
        keyboard: false,
      })
      .result.then(
        result => {},
        reason => {
          if (reason) {
            this.setValue();
          }
        }
      );
  }

  onDropdownSelectorChange(eventArgs: CAUserDto[]): void {
    this.value = new Array(...eventArgs);
    this.onChange(this.value);
  }

  onDepartmentSelectionChange(eventArgs: DepartmentDto): void {
    this.departmentId = eventArgs.id;
  }

  onDepartmentSearchInputChange(eventArgs: Event): void {
    const input = eventArgs.currentTarget as HTMLInputElement;
    const fn = this.doSearchDepartment.bind(this, input.value);

    clearTimeout(this.searchDepartmentTimer);

    this.searchDepartmentTimer = setTimeout(fn, 300);
  }

  onDepartmentSearchInputEnter(eventArgs: KeyboardEvent): void {
    const input = eventArgs.currentTarget as HTMLInputElement;

    this.doSearchDepartment(input.value);
  }

  onUserSearchInputChange(eventArgs: Event): void {
    const input = eventArgs.currentTarget as HTMLInputElement;
    const fn = this.doSearchUser.bind(this, input.value);

    clearTimeout(this.searchUserTimer);

    this.searchUserTimer = setTimeout(fn, 300);
  }

  onUserSearchInputEnter(eventArgs: KeyboardEvent): void {
    const input = eventArgs.currentTarget as HTMLInputElement;

    this.doSearchUser(input.value);
  }

  onClickClear(eventArgs: MouseEvent): void {
    this.selectedUsers = [];
  }

  changeStatusFilter(eventArgs) {
    this.currentStatus = eventArgs.label;
    if (eventArgs.id == 1) {
      this.status = 'active';
    } else if (eventArgs.id == 2) {
      this.status = 'passive';
    } else {
      this.status = 'all';
    }
  }

  openStatusSelector(eventArgs: any): void {
    setTimeout(() => {
      const dropdownId = this.selector.dropdownId;
      const dropdownEl = document.getElementById(dropdownId);
      dropdownEl.classList.add('fill-width');
    }, 1);
  }

  private doSearchDepartment(keyword): void {
    this.departmentSearchKeyword = keyword;
  }

  private doSearchUser(keyword): void {
    this.userSearchKeyword = keyword;
  }

  private setValue(): void {
    this.value = new Array(...this.selectedUsers);
    this.onChange(this.value);
  }
}
